import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import * as timService from '../../services/timService';
import imService from '../../services/imService';
import { useInit } from '../../contexts/InitContext';
import { ensureOAuth2Token } from '../../services/tokenSyncService';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const { isInitialized } = useInit();
  const location = useLocation();
  const navigate = useNavigate();
  const [tokenSyncing, setTokenSyncing] = useState(false);

  const userStr = localStorage.getItem('syntrust_user');
  const searchParams = new URLSearchParams(location.search);
  const hasLoginParams = (
    searchParams.has('is_first_login') &&
    searchParams.has('session_token') &&
    searchParams.has('user_email') &&
    searchParams.has('user_id') &&
    searchParams.has('user_name')
  );

  // useEffect: 检查并同步 OAuth2 token
  useEffect(() => {
    async function syncToken() {
      // 跳过 /callback 页面（正在处理 OAuth2 回调）
      if (location.pathname === '/callback') {
        return;
      }

      setTokenSyncing(true);
      try {
        const needsSync = await ensureOAuth2Token();
        if (needsSync) {
          // 会触发页面跳转，不需要做其他操作
          return;
        }
      } catch (error) {
        console.error('Token sync error:', error);
      } finally {
        setTokenSyncing(false);
      }
    }

    syncToken();
  }, [location.pathname]);

  // useEffect: 处理首次带参数登录
  useEffect(() => {
    const success = searchParams.get('success');
    if (!userStr && success === 'false') {
      navigate('/login?fail=1', { replace: true });
      return;
    }
    if (!userStr && hasLoginParams) {

      const guestUser = {
        id: searchParams.get('user_id'),
        name: decodeURIComponent(searchParams.get('user_name') || ''),
        email: searchParams.get('user_email'),
        role: 'user',
        inserted_at: Date.now(),
        expires_in: 1000 * 60 * 60 * 24 * 7 // 7天
      };
      localStorage.setItem('syntrust_user', JSON.stringify(guestUser));
      localStorage.setItem('syntrust_auth_token', searchParams.get('session_token') || '');
      localStorage.setItem('syntrust_login_status', 'true');
      (async () => {
        try {
          const sigData = await imService.getUserSig();
 
          if (sigData && sigData.user_id && sigData.user_sig) {
          await timService.initTIM();
            await timService.loginTIM(sigData.user_id, sigData.user_sig);
            console.log('IM登录成功，userID:', sigData.user_id, 'userSig:', sigData.user_sig);
        
          } else {
            console.error('获取IM userSig失败', sigData);
          }
        } catch (e) {
          console.warn('IM登录失败', e);
        }
        window.location.replace('/');
      })();
    }
    // eslint-disable-next-line
  }, [userStr, hasLoginParams, searchParams, navigate]);

  // useEffect: 页面刷新时自动登录IM和初始化数据
  useEffect(() => {
    async function autoLoginIMAndInit() {
      if (!userStr) return;
      let user = null;
      try {
        user = JSON.parse(userStr);
      } catch { return; }
      
      // 统一使用user_id/user_sig，兼容老数据
      const userId = user.user_id || user.id;
      const userSig = user.user_sig || user.user_signature;
      if (!userId) return;
      
      // 1. 先处理IM登录
      if (timService && typeof timService.isLogin === 'function' && !timService.isLogin()) {
        try {
          await timService.initTIM();
          if (userSig) {
            await timService.loginTIM(userId, userSig);
            console.log('IM自动登录成功', userId);
          } else {
            // 没有user_sig，重新请求
            const sigData = await imService.getUserSig();
            if (sigData && sigData.user_id && sigData.user_sig) {
              await timService.loginTIM(sigData.user_id, sigData.user_sig);
              // 写入localStorage
              const userStr2 = localStorage.getItem('syntrust_user');
              if (userStr2) {
                const userObj2 = JSON.parse(userStr2);
                userObj2.user_sig = sigData.user_sig;
                userObj2.user_id = sigData.user_id;
                localStorage.setItem('syntrust_user', JSON.stringify(userObj2));
              }
            }
          }
        } catch (e) {
          console.warn('IM自动登录失败', e);
        }
      }
      
      // 2. 初始化数据由 InitContext 统一管理，这里不需要重复处理
      console.log('IM登录完成，数据初始化由全局上下文管理');
    }
    
    autoLoginIMAndInit();
  }, [userStr]);

  // 过期判断
  let isExpired = false;
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      const now = Date.now();
      if (user.inserted_at && user.expires_in && now - user.inserted_at > user.expires_in) {
        isExpired = true;
      }
    } catch (e) {
      isExpired = true;
    }
  }

  // 条件return全部放在Hook之后
  // 🔑 新逻辑：默认不强制登录，允许用户访问页面
  // 当API需要认证时，会自动弹出内嵌登录框（由 api.js 处理）

  // 如果有登录参数，处理登录
  if (!userStr && hasLoginParams) {
    // 防止组件继续渲染
    return <></>;
  }

  // 如果登录已过期，清除数据但不跳转
  if (isExpired) {
    localStorage.removeItem('syntrust_user');
    localStorage.removeItem('syntrust_auth_token');
    localStorage.removeItem('syntrust_login_status');
    console.log('⚠️ Session expired, cleared auth data');
  }

  // 显示加载状态
  if (loading || tokenSyncing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip={tokenSyncing ? "同步登录状态..." : "加载中..."} />
      </div>
    );
  }

  // 🔑 关键变更：即使未认证也渲染children，让API拦截器处理认证
  return <>{children}</>;
};

export default ProtectedRoute; 